//Proofed
import { useState } from 'react'
import { Box, Container, Grid, Typography, Stack, Chip } from '@mui/material'
import { Link } from 'react-router-dom'
import { BookDemoModal } from '../../components/BookDemoModal'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import StarIcon from '@mui/icons-material/Star'
import { COLOR_QUATERNARY, EXECUTION_HERO_START, EXECUTION_HERO_MID, CHECKCIRCLE_SIZE, SECTION_DARK, DARK_800, BG_LIGHT, NAVBAR_BG } from '../../theme'
import { HowItWorksSection } from '../../components/HowItWorksSection'
import { TrustedPartnerSection } from '../../components/TrustedPartnerSection'
import { WaveDivider } from '../../components/WaveDivider'
import { ChallengesSection } from '../../components/ChallengesSection'
import { SolutionStack } from '../../components/SolutionStack'
import { CtaSection } from '../../components/CtaSection'
import PageMeta from '../../components/PageMeta'
import { RelatedSolutionsSection } from '../../components/RelatedSolutionsSection'
import { OutcomesSection } from '../../components/OutcomesSection'
import { SelfQualificationSection } from '../../components/SelfQualificationSection'
import {
    bidPartnerHighlights,
    bidPartnerChallenges,
    bidPartnerSolutionStack,
    bidPartnerSteps,
    bidPartnerOutcomes,
    bidPartnerQualifiers,
    bidPartnerFaqs,
    bidPartnerRelated,
} from '../../common/SolutionBidPartnerContent'
import { FaqSection } from '../../components/FaqSection'
// import { bidPartnerTestimonials } from '../../common/Testimonials'

const C = COLOR_QUATERNARY

export default function FindBidPartner() {
    const [demoOpen, setDemoOpen] = useState(false)

    return (
        <Box>
            <PageMeta
                title="Find a Dedicated Bid Partner"
                description="Connect with experienced UK public sector bid writers through the CFP partner network. Scale your bid writing capacity, get expert reviews and access sector-specific knowledge without a full-time hire."
                canonical="/solutions/find-a-dedicated-bid-partner"
            />
            {/* ── 1. Hero  light left / dark panel right ─────────────────────── */}
            <Box sx={{
                background: `linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`,
                py: { xs: 6, md: 10 },
            }}>
                <Container maxWidth="lg">

                    <Grid container spacing={5} alignItems="stretch">
                        {/* Left  copy */}
                        <Grid item xs={12} md={6}>
                            <Chip label="Execution Support · Outsourced Bid Writing" size="small" sx={{
                                mb: 3, backgroundColor: `${C}12`, color: C,
                                border: `1px solid ${C}40`, fontWeight: 700,
                                fontSize: 18,
                            }} />
                            <Typography variant="h1" sx={{ fontSize: { xs: '36px', md: '52px' }, mb: 2, color: NAVBAR_BG, lineHeight: 1.15 }}>
                                Scale Your Bid Writing With{' '}
                                <Box component="span" sx={{ color: C }}>Specialist Writers</Box>
                            </Typography>
                            <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 400, mb: 4, lineHeight: 1.6 }}>
                                Access experienced public sector bid writers who know your sector, understand how buyers score and can take your submissions from good to winning
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <FancyGoldButton to="/pricing" color={C} endIcon={<ArrowForwardIcon />}>
                                    Get Started
                                </FancyGoldButton>
                                <FancyLightButton onClick={() => setDemoOpen(true)} color={C}>
                                    Book a Discovery Call
                                </FancyLightButton>
                            </Stack>
                        </Grid>

                        {/* Right  dark highlights panel */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                background: `linear-gradient(135deg, ${EXECUTION_HERO_START} 0%, ${EXECUTION_HERO_MID} 100%)`,
                                border: `1px solid ${C}30`,
                                borderRadius: 3,
                                p: { xs: 3, md: 4 },
                                // height: '100%',
                            }}>
                                <Typography variant="caption" sx={{
                                    color: C, textTransform: 'uppercase',
                                    letterSpacing: '1px', fontWeight: 700,
                                }}>
                                    With this solution you'll be able to
                                </Typography>
                                <Stack spacing={1.75} sx={{ mt: 2.5 }}>
                                    {bidPartnerHighlights.map(h => (
                                        <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <CheckCircleIcon sx={{ color: C, fontSize: CHECKCIRCLE_SIZE, mt: 0.15, flexShrink: 0 }} />
                                            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', lineHeight: 1.6 }}>{h}</Typography>
                                        </Box>
                                    ))}
                                </Stack>

                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <WaveDivider top={`linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`} bottom={DARK_800} />
            {/* ── 2. Challenges  dark ─────────────────────────────────────────── */}
            <ChallengesSection
                title="Three reasons bids are won or lost before the deadline"
                subtitle="Every one of these is addressable with the right specialist writer alongside you"
                items={bidPartnerChallenges}
                color={C}
            />

            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* ── 3. Solution stack  light ────────────────────────────────────── */}
            <SolutionStack
                subtitle="A matched specialist writer, hands-on bid writing support and quality reviewed submissions, giving your pipeline the expertise it needs without a permanent hire"
                items={bidPartnerSolutionStack}
                color={C}
            />

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 4. Journey  dark (shared HowItWorksSection) ─────────────────── */}
            <HowItWorksSection
                title="How bid writer matching works"
                subtitle="From brief to submission, with a specialist who knows your sector at every stage."
                steps={bidPartnerSteps}
                dark={true}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            {/* ── 5. Outcomes  light ──────────────────────────────────────────── */}
            <OutcomesSection
                title="What you'll have"
                subtitle="With a specialist bid writer matched to your pipeline, this is what changes"
                items={bidPartnerOutcomes}
                color={C}
            />

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 6. Self-qualification  dark ─────────────────────────────────── */}
            <SelfQualificationSection
                items={bidPartnerQualifiers}
                color={C}
                onDemoClick={() => setDemoOpen(true)}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            {/* ── 9. Related solutions  light ─────────────────────────────────── */}
            <RelatedSolutionsSection
                subtitle="Whether you want to build in-house capability or explore other solutions, here's where to look next"
                items={bidPartnerRelated}
                color={C}
            />


            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 8. FAQ  dark ────────────────────────────────────────────────── */}
            <FaqSection
                title='Common questions'
                subtitle="From businesses who've used specialist bid writing support to improve their submissions and win rates"
                items={bidPartnerFaqs}
                color={DARK_800}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            <TrustedPartnerSection />

            <CtaSection
                title="Ready to win more bids with specialist support?"
                subtitle="Book a no-obligation discovery call. We'll explain how the matching process works and confirm the right writer for your pipeline"
                onDemoClick={() => setDemoOpen(true)}
            />

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
